/**
 * Integrytas Group - Base App (MOBILE)
 * Page: UPP.DEFINITIONS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UppDefinitionManagerPage.tsx. A
 * DISTINCT presentation (dense card list + facet chips + status pills +
 * bottom-sheet detail + edit sheet) over the SAME data/logic layer:
 *   - reads via apiDb (upp_definition, plus a upp_field roll-up for the
 *     "field mappings" count shown per definition),
 *   - the scalar edit (name / direction / format / on-invalid / entity / active)
 *     routes through the Common Routine (routedUpdate) — §2 single write path.
 * Field-mapping authoring stays on the desktop builder; the count is read-only
 * here. It never re-hosts the desktop grid; §16.
 *
 * direction / output_format / on_invalid are CHECK-enum literals (§17.B11) —
 * their labels go through t() labelled constants, never the bare stored code.
 * upp_definition has no _tl table (name is a direct column), so there is no FK
 * resolution to do here.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, FileUp, FileDown, SquarePen, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'UPP.DEFINITIONS';

type Direction = 'EXPORT' | 'IMPORT';
type OnInvalid = 'FLAG' | 'REJECT';

interface MobileDefRow {
  upp_definition_id: number;
  internal_code: string;
  name: string;
  direction: Direction;
  entity_name: string | null;
  output_format: string;
  on_invalid: OnInvalid;
  is_active: 'Y' | 'N';
  field_count: number;
}

type DirFacet = 'all' | Direction;
type StatusFacet = 'all' | 'active' | 'inactive';

// §17.B11: CHECK-enum labels defined once as labelled constants, never bare
// literals scattered through the JSX. The stored value stays the code.
interface EnumOption { value: string; labelKey: string; fallback: string }
const DIRECTION_OPTIONS: EnumOption[] = [
  { value: 'EXPORT', labelKey: 'mobile.upp.direction.EXPORT', fallback: 'Export' },
  { value: 'IMPORT', labelKey: 'mobile.upp.direction.IMPORT', fallback: 'Import' },
];
const FORMAT_OPTIONS: EnumOption[] = [
  { value: 'CSV', labelKey: 'mobile.upp.format.CSV', fallback: 'CSV' },
  { value: 'XLSX', labelKey: 'mobile.upp.format.XLSX', fallback: 'XLSX' },
  { value: 'PDF', labelKey: 'mobile.upp.format.PDF', fallback: 'PDF' },
  { value: 'JSON', labelKey: 'mobile.upp.format.JSON', fallback: 'JSON' },
];
const ON_INVALID_OPTIONS: EnumOption[] = [
  { value: 'FLAG', labelKey: 'mobile.upp.on_invalid.FLAG', fallback: 'Flag' },
  { value: 'REJECT', labelKey: 'mobile.upp.on_invalid.REJECT', fallback: 'Reject' },
];

export function MobileUppDefinitionManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<MobileDefRow[]>([]);
  const [filter, setFilter] = useState('');
  const [dirFacet, setDirFacet] = useState<DirFacet>('all');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<MobileDefRow | null>(null);
  const [editing, setEditing] = useState<MobileDefRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve a CHECK-enum code to its translated label (§17.B11).
  const enumLabel = (options: EnumOption[], value: string): string => {
    const opt = options.find((o) => o.value === value);
    return opt ? t(opt.labelKey, opt.fallback) : value;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data: rawDefs, error: dErr } = await apiDb
        .from('upp_definition')
        .select('upp_definition_id, internal_code, name, direction, entity_name, output_format, on_invalid, is_active')
        .order('internal_code');
      if (dErr) throw dErr;

      // Roll up how many field mappings each definition owns — a scannable,
      // mobile-friendly summary of the desktop builder's mapping grid.
      const { data: rawFields, error: fErr } = await apiDb
        .from('upp_field')
        .select('upp_definition_id');
      if (fErr) throw fErr;
      const counts: Record<number, number> = {};
      for (const f of (rawFields ?? []) as { upp_definition_id: number }[]) {
        counts[f.upp_definition_id] = (counts[f.upp_definition_id] ?? 0) + 1;
      }

      type RawDef = Omit<MobileDefRow, 'field_count'>;
      const mapped: MobileDefRow[] = ((rawDefs ?? []) as RawDef[]).map((d) => ({
        ...d,
        field_count: counts[d.upp_definition_id] ?? 0,
      }));
      setRows(mapped);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (dirFacet !== 'all' && r.direction !== dirFacet) return false;
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.entity_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, dirFacet, statusFacet]);

  const dirFacets: { key: DirFacet; label: string }[] = [
    { key: 'all', label: t('mobile.upp.all', 'All') },
    { key: 'EXPORT', label: t('mobile.upp.direction.EXPORT', 'Export') },
    { key: 'IMPORT', label: t('mobile.upp.direction.IMPORT', 'Import') },
  ];
  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.upp.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const fieldsLabel = (n: number) => t('mobile.upp.fields_count', { count: n, defaultValue: `${n} fields` });

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.upp.title', 'Import & Export')}
        description={t('mobile.upp.description', 'Import and export definitions, their format and field mappings; build mappings on desktop.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.upp.search', 'Search definitions')}
          aria-label={t('mobile.upp.search', 'Search definitions')}
        />
      </div>

      <MobileFilterChips
        chips={dirFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => dirFacet === v}
        onSelect={(v) => setDirFacet(v as DirFacet)}
      />

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.upp.none', 'No definitions match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.upp_definition_id}
              lead={r.direction === 'EXPORT' ? <FileUp /> : <FileDown />}
              leadTone={r.direction === 'EXPORT' ? 'green' : 'blue'}
              title={r.name || r.internal_code}
              subtitle={`${r.internal_code} · ${enumLabel(FORMAT_OPTIONS, r.output_format)} · ${fieldsLabel(r.field_count)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.upp.detail', 'Definition detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{selected.direction === 'EXPORT' ? <FileUp size={20} /> : <FileDown size={20} />}</span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.upp.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.upp.direction_label', 'Direction')} value={enumLabel(DIRECTION_OPTIONS, selected.direction)} />
              <MobileDetailRow label={t('mobile.upp.format_lbl', 'Format')} value={enumLabel(FORMAT_OPTIONS, selected.output_format)} />
              <MobileDetailRow label={t('mobile.upp.on_invalid_lbl', 'On invalid')} value={enumLabel(ON_INVALID_OPTIONS, selected.on_invalid)} />
              <MobileDetailRow label={t('mobile.upp.entity', 'Entity')} value={selected.entity_name || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.upp.fields', 'Field mappings')} value={fieldsLabel(selected.field_count)} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.upp.edit', 'Edit')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileUppDefinitionEditSheet
        session={session}
        def={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

interface MobileUppDefinitionEditSheetProps {
  session: AuthSession;
  def: MobileDefRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit sheet for a definition's scalar fields. The code (internal_code) is
 * immutable and field-mapping authoring stays on desktop; here we edit
 * name / direction / format / on-invalid / entity / active and save through the
 * Common Routine (routedUpdate) — §2 single write path.
 */
function MobileUppDefinitionEditSheet({ session, def, onClose, onSaved }: MobileUppDefinitionEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [direction, setDirection] = useState<Direction>('EXPORT');
  const [format, setFormat] = useState('CSV');
  const [onInvalid, setOnInvalid] = useState<OnInvalid>('FLAG');
  const [entity, setEntity] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different definition is opened.
  useEffect(() => {
    if (!def) return;
    setName(def.name);
    setDirection(def.direction);
    setFormat(def.output_format);
    setOnInvalid(def.on_invalid);
    setEntity(def.entity_name ?? '');
    setActive(def.is_active === 'Y');
    setError(null);
  }, [def]);

  const save = async () => {
    if (!def) return;
    if (!name.trim()) { setError(t('mobile.upp.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'upp_definition', 'upp_definition_id', def.upp_definition_id, {
      name: name.trim(),
      direction,
      output_format: format,
      on_invalid: onInvalid,
      entity_name: entity.trim() || null,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = def !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.upp.edit', 'Edit definition')}>
        <div className="m-grabber" />
        {def && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{direction === 'EXPORT' ? <FileUp size={20} /> : <FileDown size={20} />}</span>
              <span>
                <div className="m-nm">{t('mobile.upp.edit', 'Edit definition')}</div>
                <div className="m-em">{def.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-upp-name">{t('mobile.upp.name', 'Name')}</label>
                <input
                  id="m-upp-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-upp-direction">{t('mobile.upp.direction_label', 'Direction')}</label>
                <select id="m-upp-direction" className="m-sel" value={direction} onChange={(e) => setDirection(e.target.value as Direction)}>
                  {DIRECTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-upp-format">{t('mobile.upp.format_lbl', 'Format')}</label>
                <select id="m-upp-format" className="m-sel" value={format} onChange={(e) => setFormat(e.target.value)}>
                  {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-upp-on-invalid">{t('mobile.upp.on_invalid_lbl', 'On invalid')}</label>
                <select id="m-upp-on-invalid" className="m-sel" value={onInvalid} onChange={(e) => setOnInvalid(e.target.value as OnInvalid)}>
                  {ON_INVALID_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-upp-entity">{t('mobile.upp.entity', 'Entity (optional)')}</label>
                <input
                  id="m-upp-entity"
                  className="m-inp"
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  placeholder="csm_user_master"
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.upp.active_hint', 'Definition can run')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.upp.fields', 'Field mappings')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.upp.fields_count', { count: def.field_count, defaultValue: `${def.field_count} fields` })}
                  <span className="m-formrow-hint"> · {t('mobile.upp.fields_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
